export type GatewayResponse<T = any> = {
  data: T;
  headers: Record<string, string>;
  ok: boolean;
  status: number;
};

export type RestErrorResponseData =
  | {
      errors?: unknown;
      message?: string;
      [key: string]: unknown;
    }
  | Blob
  | string
  | null;

export type RestResponseType = 'blob' | 'json' | 'text';

type RequestParams = Record<string, unknown>;

type RequestData = BodyInit | Record<string, unknown> | unknown[];

const DEFAULT_VALID_STATUSES = [200, 201, 204] as const;

function normalizeHeaders(headers: Headers): Record<string, string> {
  return Object.fromEntries(headers.entries());
}

function isAbsoluteUrl(url: string) {
  return /^https?:\/\//.test(url);
}

function createQueryString(params?: RequestParams) {
  const searchParams = new URLSearchParams();

  if (!params) {
    return '';
  }

  Object.entries(params).forEach(([key, value]) => {
    (Array.isArray(value) ? value.flat(Infinity) : [value]).forEach((item) => {
      if (item !== undefined && item !== null) {
        searchParams.append(key, String(item));
      }
    });
  });

  const queryString = searchParams.toString();

  return queryString ? `?${queryString}` : '';
}

function appendQueryString(url: string, queryString: string) {
  if (!queryString) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : ''}${queryString.replace('?', url.includes('?') ? '' : '?')}`;
}

function createRequestUrl({
  baseURL,
  params,
  url,
}: {
  baseURL?: string;
  params?: RequestParams;
  url: string;
}) {
  const queryString = createQueryString(params);

  if (isAbsoluteUrl(url)) {
    return appendQueryString(url, queryString);
  }

  if (!baseURL) {
    return appendQueryString(url, queryString);
  }

  const normalizedBaseUrl = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

  return appendQueryString(
    url ? `${normalizedBaseUrl}${normalizedUrl}` : normalizedBaseUrl,
    queryString,
  );
}

function createRequestBody(data?: RequestData) {
  if (
    data === undefined ||
    typeof data === 'string' ||
    data instanceof Blob ||
    data instanceof FormData ||
    data instanceof URLSearchParams ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data)
  ) {
    return data;
  }

  return JSON.stringify(data);
}

function getResponseMessage(status?: number, data?: RestErrorResponseData, fallback?: string) {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (
    data &&
    typeof data === 'object' &&
    !(data instanceof Blob) &&
    typeof data.message === 'string' &&
    data.message.trim()
  ) {
    return data.message;
  }
  if (fallback) {
    return fallback;
  }
  if (status) {
    return `Unexpected status code: ${status}`;
  }
  return 'Request failed';
}

function isAbortError(error: unknown, signal?: AbortSignal) {
  return (
    signal?.aborted === true ||
    (typeof DOMException !== 'undefined' &&
      error instanceof DOMException &&
      error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

export class RestError extends Error {
  status?: number;
  response: {
    data: RestErrorResponseData;
    status?: number;
  };

  constructor(
    message: string,
    status?: number,
    data: RestErrorResponseData = null,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'RestError';
    this.status = status;
    this.response = {
      status,
      data,
    };
  }
}

function createRestError({
  cause,
  data = null,
  fallbackMessage,
  status,
}: {
  cause?: unknown;
  data?: RestErrorResponseData;
  fallbackMessage?: string;
  status?: number;
}) {
  return new RestError(getResponseMessage(status, data, fallbackMessage), status, data, { cause });
}

async function parseResponseData(
  response: Response,
  responseType: RestResponseType,
): Promise<RestErrorResponseData | Blob> {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.startsWith('application/json');

  if (isJson) {
    const text = await response.text();
    if (!text) {
      return null;
    }

    return JSON.parse(text);
  }

  if (responseType === 'blob') {
    return response.blob();
  }

  const text = await response.text();
  return text || null;
}

export async function performRestRequest<T = any>({
  baseURL,
  body,
  data,
  headers,
  method,
  params,
  responseType = 'json',
  signal,
  url,
  validStatuses = DEFAULT_VALID_STATUSES,
}: {
  baseURL?: string;
  body?: BodyInit | null;
  data?: RequestData;
  headers: Record<string, string>;
  method?: string;
  params?: RequestParams;
  responseType?: RestResponseType;
  signal?: AbortSignal;
  url: string;
  validStatuses?: readonly number[];
}): Promise<GatewayResponse<T>> {
  try {
    const response = await fetch(createRequestUrl({ baseURL, params, url }), {
      body: body !== undefined ? body : createRequestBody(data),
      headers,
      method,
      signal,
    });

    let responseData: RestErrorResponseData | Blob;
    try {
      responseData = await parseResponseData(response, responseType);
    } catch (error) {
      if (isAbortError(error, signal)) {
        throw error;
      }

      throw createRestError({
        cause: error,
        fallbackMessage: 'Failed to parse response body',
        status: response.status,
      });
    }

    if (!response.ok || !validStatuses.includes(response.status)) {
      throw createRestError({
        data: responseData,
        status: response.status,
      });
    }

    return {
      data: responseData as T,
      headers: normalizeHeaders(response.headers),
      ok: response.ok,
      status: response.status,
    };
  } catch (error) {
    if (error instanceof RestError || isAbortError(error, signal)) {
      throw error;
    }

    throw createRestError({
      cause: error,
      fallbackMessage: error instanceof Error ? error.message : undefined,
    });
  }
}
